import { Component, createRef } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import * as XLSX from 'xlsx';

const BRAND = {
  red:    "#dc3545",
  ink:    "#1B1B1F",
  muted:  "#6c757d",
  bg:     "#f8f9fa",
  border: "#eef0f2",
};

class Districts extends Component {
  constructor(props) {
    super(props);
    this.state = {
      districts: [],
      loading: true,
      submitting: false,
      importing: false,
      error: "",
      success: "",
      showAdd: false,
      newDistrict: "",
      showEdit: false,
      editId: null,
      editName: "",
      editStatus: "active",
      deleteId: null,
    };
    this.fileInputRef = createRef();
  }
  

  componentDidMount() { this.fetchDistricts(); }

  showAlert(type, msg) {
    this.setState({ [type]: msg });
    setTimeout(() => this.setState({ [type]: "" }), 3000);
  }
  formatDate(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d)) return "—";
    const day = String(d.getDate()).padStart(2, "0");
    const month = d.toLocaleString("en-US", { month: "short" });
    const year = String(d.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  }
  async fetchDistricts() {
    this.setState({ loading: true });
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/districts`, { credentials: "include" });
      const data = await res.json();
      this.setState({ districts: data.data || [], loading: false });
    } catch {
      this.setState({ loading: false });
    }
  }

  // ── Export ────────────────────────────────────────────────
  handleExport() {
    const { districts } = this.state;
    if (!districts.length) return this.showAlert("error", "No districts to export.");

    const header = "name,status";
    const rows = districts.map(d => `${d.name},${d.status}`);
    const csv = [header, ...rows].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "districts.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Import ────────────────────────────────────────────────
  handleImportClick() {
    this.fileInputRef.current.value = "";
    this.fileInputRef.current.click();
  }

  async handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isCSV = fileName.endsWith(".csv");
    const isExcel = fileName.endsWith(".xlsx") || fileName.endsWith(".xls");

    if (!isCSV && !isExcel) {
      return this.showAlert("error", "Please select a .csv, .xlsx, or .xls file.");
    }

    this.setState({ importing: true });
    try {
      let districts = [];

      if (isCSV) {
        const text = await file.text();
        const lines = text.trim().split("\n").filter(Boolean);
        const dataLines = lines[0].toLowerCase().startsWith("name")
          ? lines.slice(1)
          : lines;

        districts = dataLines.map(line => {
          const [name = "", status = "active"] = line.split(",").map(s => s.trim());
          return {
            name,
            status: ["active", "inactive"].includes(status.toLowerCase())
              ? status.toLowerCase()
              : "active",
          };
        }).filter(d => d.name);

      } else {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        districts = rows.map(row => {
          const name = String(row["name"] || row["Name"] || "").trim();
          const status = String(row["status"] || row["Status"] || "active").trim().toLowerCase();
          return {
            name,
            status: ["active", "inactive"].includes(status) ? status : "active",
          };
        }).filter(d => d.name);
      }

      if (!districts.length) {
        return this.showAlert("error", "No valid district rows found in the file.");
      }

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/districts/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ districts }),
      });
      const data = await res.json();

      if (res.ok) {
        this.showAlert("success", data.message || "Districts imported successfully.");
        this.fetchDistricts();
      } else {
        this.showAlert("error", data.message || "Import failed.");
      }
    } catch {
      this.showAlert("error", "Failed to read or upload file.");
    } finally {
      this.setState({ importing: false });
    }
  }

  // ── Add / Update / Delete ────────────────────────────────
  async handleAdd(e) {
    e.preventDefault();
    const { newDistrict } = this.state;
    if (!newDistrict.trim()) return this.showAlert("error", "District name is required.");
    this.setState({ submitting: true });
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/districts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newDistrict.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        this.setState({ newDistrict: "", showAdd: false });
        this.showAlert("success", "District added successfully.");
        this.fetchDistricts();
      } else {
        this.showAlert("error", data.message);
      }
    } catch {
      this.showAlert("error", "Something went wrong.");
    } finally {
      this.setState({ submitting: false });
    }
  }

  async handleUpdate(e) {
    e.preventDefault();
    const { editId, editName, editStatus } = this.state;
    if (!editName.trim()) return this.showAlert("error", "District name is required.");
    this.setState({ submitting: true });
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/districts/${editId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: editName, status: editStatus }),
      });
      const data = await res.json();
      if (res.ok) {
        this.setState({ showEdit: false, editId: null });
        this.showAlert("success", "District updated successfully.");
        this.fetchDistricts();
      } else {
        this.showAlert("error", data.message);
      }
    } catch {
      this.showAlert("error", "Something went wrong.");
    } finally {
      this.setState({ submitting: false });
    }
  }

  async handleDelete(id) {
    this.setState({ submitting: true });
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/districts/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (res.ok) {
        this.setState({ deleteId: null });
        this.showAlert("success", "District deleted.");
        this.fetchDistricts();
      } else {
        this.showAlert("error", data.message);
      }
    } catch {
      this.showAlert("error", "Something went wrong.");
    } finally {
      this.setState({ submitting: false });
    }
  }

  openEdit(district) {
    this.setState({ showEdit: true, editId: district.id, editName: district.name, editStatus: district.status });
  }

  // ── Modal ─────────────────────────────────────────────────
  renderModal({ title, onClose, onSubmit, children, submitting }) {
    return (
      <div style={{
        position: "fixed", inset: 0, zIndex: 2000,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }} onClick={onClose}>
        <div style={{
          background: "#fff", borderRadius: 12,
          width: "100%", maxWidth: 440,
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          overflow: "hidden",
        }} onClick={e => e.stopPropagation()}>
          <div style={{
            padding: "16px 20px", borderBottom: "1px solid #f0f0f0",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <h6 style={{ margin: 0, fontWeight: 700 }}>{title}</h6>
            <button onClick={onClose} style={{
              background: "none", border: "none", fontSize: 20,
              cursor: "pointer", color: "#888", lineHeight: 1,
            }}>×</button>
          </div>
          <form onSubmit={onSubmit}>
            <div style={{ padding: 20 }}>{children}</div>
            <div style={{
              padding: "12px 20px", borderTop: "1px solid #f0f0f0",
              display: "flex", justifyContent: "flex-end", gap: 10,
            }}>
              <button type="button" onClick={onClose} style={{
                padding: "8px 18px", borderRadius: 8, border: "1px solid #ddd",
                background: "#f8f9fa", fontWeight: 600, cursor: "pointer",
              }}>Cancel</button>
              <button type="submit" disabled={submitting} style={{
                padding: "8px 20px", borderRadius: 8, border: "none",
                background: "#dc3545", color: "#fff", fontWeight: 600, cursor: "pointer",
              }}>
                {submitting ? <span className="spinner-border spinner-border-sm" /> : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  render() {
    const {
      districts, loading, submitting, importing,
      error, success,
      showAdd, newDistrict,
      showEdit, editName, editStatus,
      deleteId,
    } = this.state;

    return (
        <div className="pt-5 mt-4" style={{ background: BRAND.bg, minHeight: "100vh" }}>
        <div className="container-fluid py-4 px-4 px-lg-5">

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
            <div>
              <h4 className="fw-bold mb-1">District Management</h4>
              <p className="text-muted small mb-0">Add and manage districts.</p>
            </div>

            <div className="d-flex gap-2">
              <input
                ref={this.fileInputRef}
                type="file"
                accept=".csv, .xlsx, .xls"
                style={{ display: "none" }}
                onChange={e => this.handleFileChange(e)}
              />

              <button
                onClick={() => this.handleImportClick()}
                disabled={importing}
                style={{
                  background: "#fff", color: "#dc3545",
                  border: "1.5px solid #dc3545",
                  borderRadius: 8, padding: "9px 16px",
                  fontWeight: 600, cursor: "pointer", fontSize: 14,
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                {importing
                  ? <span className="spinner-border spinner-border-sm text-danger" />
                  : <i className="bi bi-upload" />
                }
                Import
              </button>

              <button
                onClick={() => this.handleExport()}
                style={{
                  background: "#fff", color: "#198754",
                  border: "1.5px solid #198754",
                  borderRadius: 8, padding: "9px 16px",
                  fontWeight: 600, cursor: "pointer", fontSize: 14,
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                <i className="bi bi-download" /> Export
              </button>

              <button
                onClick={() => this.setState({ showAdd: true, newDistrict: "" })}
                style={{
                  background: "#dc3545", color: "#fff", border: "none",
                  borderRadius: 8, padding: "9px 20px",
                  fontWeight: 600, cursor: "pointer", fontSize: 14,
                }}
              >
                + Add District
              </button>
            </div>
          </div>

          {error && <div className="alert alert-danger py-2 small">⚠️ {error}</div>}
          {success && <div className="alert alert-success py-2 small">✅ {success}</div>}

          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0">All Districts</h6>
                <span className="badge bg-danger">{districts.length} total</span>
              </div>
              {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-danger" /></div>
              ) : districts.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <div style={{ fontSize: 40 }}>🗺️</div>
                  <p className="mt-2">No districts added yet.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>#</th><th>District Name</th><th>Status</th><th>Added On</th><th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {districts.map((district, i) => (
                        <tr key={district.id}>
                          <td className="text-muted small">{i + 1}</td>
                          <td className="fw-semibold">{district.name}</td>
                          <td>
                            <span className={`badge bg-${district.status === "active" ? "success" : "secondary"}`}>
                              {district.status.charAt(0).toUpperCase() + district.status.slice(1)}
                            </span>
                          </td>
                          <td className="text-muted small">{this.formatDate(district.created_at)}</td>

                          <td>
                            {deleteId === district.id ? (
                              <div className="d-flex gap-2 align-items-center">
                                <span className="text-danger small fw-semibold">Delete?</span>
                                <button className="btn btn-danger btn-sm"
                                  onClick={() => this.handleDelete(district.id)}
                                  disabled={submitting}>Yes</button>
                                <button className="btn btn-secondary btn-sm"
                                  onClick={() => this.setState({ deleteId: null })}>No</button>
                              </div>
                            ) : (
                              <div className="d-flex gap-2">
                                <button className="btn btn-link p-0 me-2 text-secondary"
                                  onClick={() => this.openEdit(district)} title="Edit">
                                  <i className="bi bi-pencil fs-8" />
                                </button>
                                <button className="btn btn-link p-0 text-danger"
                                  onClick={() => this.setState({ deleteId: district.id })} title="Delete">
                                  <i className="bi bi-trash fs-8" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {showAdd && this.renderModal({
          title: "Add New District", submitting,
          onClose: () => this.setState({ showAdd: false }),
          onSubmit: e => this.handleAdd(e),
          children: (
            <div>
              <label className="form-label fw-semibold small">District Name</label>
              <input type="text" className="form-control" placeholder="e.g. Rawalpindi District"
                value={newDistrict} onChange={e => this.setState({ newDistrict: e.target.value })} autoFocus />
            </div>
          ),
        })}

        {showEdit && this.renderModal({
          title: "Edit District", submitting,
          onClose: () => this.setState({ showEdit: false }),
          onSubmit: e => this.handleUpdate(e),
          children: (
            <>
              <div className="mb-3">
                <label className="form-label fw-semibold small">District Name</label>
                <input type="text" className="form-control" value={editName}
                  onChange={e => this.setState({ editName: e.target.value })} autoFocus />
              </div>
              <div>
                <label className="form-label fw-semibold small">Status</label>
                <select className="form-select" value={editStatus}
                  onChange={e => this.setState({ editStatus: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </>
          ),
        })}
      </div>
    );
  }
}

export default Districts;